
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import DailyNumberDisplayerFilterable from '@/components/reports-v3/components/filterables/DailyNumberDisplayerFilterable.vue';
import FilterWidget from '@/components/reports-v3/components/codedWidgets/shared/FilterWidget.vue';
import {getTableName, getSqlColumnRef, isComputedProperty} from '@/worker/db/sqlHelpers';
import IncludeCreditNoteToggle from '@/components/IncludeCreditNoteToggle.vue';
import IncludeDebitNoteToggle from '@/components/IncludeDebitNoteToggle.vue';
import IncludeTaxToggle from '@/components/IncludeTaxToggle.vue';
import SlPhModuleOnlyToggle from '@/components/SlPhModuleOnlyToggle.vue';
import moment from 'moment';
import settingx from '@/store/modules/settingx';
import {PermissionsGroup} from '@/store/models.def';

@Component({
	components: {
		DailyNumberDisplayerFilterable,
		IncludeCreditNoteToggle,
		IncludeDebitNoteToggle,
		IncludeTaxToggle,
		SlPhModuleOnlyToggle,
	},
})
export default class DashboardEstimateProfitNumberDisplayer extends FilterWidget {
    public get permissionIds(): PermissionsGroup[] {
        return ['dashboard'];
    }

	public todayAmount = 0;
	public yesterdayAmount = 0;

	public get includeCreditNote() {
		return settingx.now.includeCreditNote;
	}

	public get includeDebitNote() {
		return settingx.now.includeDebitNote;
	}

	public get includeTax() {
		return settingx.now.includeTax;
	}

	public get slPhModuleOnly() {
		return settingx.now.slPhModuleOnly;
	}

	public get expensiveHook() {
		const { selectedAsOfDate, includeCreditNote, includeDebitNote, includeTax, slPhModuleOnly } = this;
		return JSON.stringify([selectedAsOfDate, includeCreditNote, includeDebitNote, includeTax, slPhModuleOnly]);
	}

	public async expensiveCalc() {
		this.generateLoadingText(0.2, 0.3);

		const todayStart = moment(this.selectedAsOfDate).startOf('day').valueOf();
		const todayEnd = moment(this.selectedAsOfDate).endOf('day').valueOf();
		const sevenDaysAgo = moment(this.selectedAsOfDate).add(-7, 'day').startOf('day').valueOf();

		const profitKey = this.slPhModuleOnly
			? (this.includeTax ? 'profitIncludeTax' : 'profit')
			: (this.includeTax ? 'arProfitIncludeTax' : 'arProfit');
		const profitRef = getSqlColumnRef('dashboardSales', profitKey);
		const profitExpr = isComputedProperty(profitKey) ? profitRef : `TRY_CAST(${profitRef} AS DOUBLE)`;
		const table = getTableName('dashboardSales');

		const jobId = this.currentJobId;
		const queryHead = await this._ensureTablesLoaded(['dashboardSales']);
		this.checkInterrupted(jobId);
		this.generateLoadingText(0.3, 0.6);

		const [todayResult, weekResult] = await Promise.all([
			queryHead.rawQuery(`
				SELECT
					SUM(CASE WHEN "DOCTYPE" IN ('IV','CS') THEN ${profitExpr} ELSE 0 END) as sales,
					SUM(CASE WHEN "DOCTYPE" = 'DN' THEN ${profitExpr} ELSE 0 END) as dn,
					SUM(CASE WHEN "DOCTYPE" = 'CN' THEN ${profitExpr} ELSE 0 END) as cn
				FROM ${table}
				WHERE TRY_CAST("DOCDATE" AS BIGINT) BETWEEN ${todayStart} AND ${todayEnd}
			`),
			queryHead.rawQuery(`
				SELECT
					SUM(CASE WHEN "DOCTYPE" IN ('IV','CS') THEN ${profitExpr} ELSE 0 END) as sales,
					SUM(CASE WHEN "DOCTYPE" = 'DN' THEN ${profitExpr} ELSE 0 END) as dn,
					SUM(CASE WHEN "DOCTYPE" = 'CN' THEN ${profitExpr} ELSE 0 END) as cn
				FROM ${table}
				WHERE TRY_CAST("DOCDATE" AS BIGINT) BETWEEN ${sevenDaysAgo} AND ${todayEnd}
			`),
		]);
		this.checkInterrupted(jobId);

		const today = todayResult[0] || {};
		const week = weekResult[0] || {};

		this.todayAmount = (Number(today.sales) || 0)
			+ (this.includeDebitNote ? (Number(today.dn) || 0) : 0)
			- (this.includeCreditNote ? (Number(today.cn) || 0) : 0);

		const weekTotal = (Number(week.sales) || 0)
			+ (this.includeDebitNote ? (Number(week.dn) || 0) : 0)
			- (this.includeCreditNote ? (Number(week.cn) || 0) : 0);
		this.yesterdayAmount = weekTotal / 7;

		this.generateLoadingText(1);
		this.saveHistory('todayAmount', 'yesterdayAmount');
	}
}
